import React from 'react'
import { InputAdd } from './InputAdd'
import { validateEmail } from '../../../lib/utilsCommon'
import { ACL_OTHER, IAcl, PERMISSIONS } from '../../../models/AbstractCatalogConfig'
import styles from './InputCatalogACL.module.scss'
import { ColumnsType } from 'antd/es/table'
import { Table } from './Table'
import { Button, Checkbox } from 'antd'
import produce from 'immer'
import { DeleteOutlined } from '@ant-design/icons'
import { store } from '../../../store'
import { t } from '../../../config/i18n'

interface Props {
  hideInput?: boolean
  hideTable?: boolean
  className?: string
  disabled?: boolean
  value?: IAcl[]
  onChange?: (value: IAcl[]) => void
}

export const InputCatalogACL = ({ hideInput, hideTable, className, disabled, value = [], onChange = () => {} }: Props) => {
  const aclEmails = React.useMemo(() => value?.map(i => i.profileID) || [], [value])

  const onEmailsAdd = React.useCallback((emails: string[]) => {
    for (const email of emails) {
      if (!aclEmails.includes(email)) {
        const creator = value.find(i => i.permissions.includes(PERMISSIONS.CREATOR))
        const other = value.find(i => i.profileID === ACL_OTHER)
        const rest = value.filter(i => i !== creator && i !== other)
        const next = [
          creator,
          ...rest,
          { profileID: email, permissions: [...defaultPermissions] },
          other,
        ].filter(i => i)
        if (!disabled) {
          onChange(next as IAcl[])
        }
      }
    }
  }, [disabled, aclEmails, value, onChange])

  const onDelete = React.useCallback((item: IAcl) => {
    !disabled && onChange(value?.filter(i => i !== item))
  }, [disabled, value, onChange])

  const createCheckboxProps = (acl: IAcl, permission: PERMISSIONS) => ({
    disabled: acl.permissions.includes(PERMISSIONS.CREATOR) || disabled,
    checked: acl.permissions.includes(PERMISSIONS.CREATOR) || acl.permissions.includes(permission),
    onChange: (e: any) => {
      if (!disabled) {
        const checked = e.target.checked
        const nextValue = updateItem(value, acl.profileID, permission, checked)
        onChange(nextValue)
      }
    },
  })

  const columns: ColumnsType<IAcl> = [
    {
      dataIndex: 'profileID',
      title: 'E-mail',
      render: (_, item) => item.profileID === ACL_OTHER ? t.catalog.aclOther : item.profileID,
    },
    {
      key: 'catalog_read',
      title: t.catalog.aclCatalogRead,
      align: 'center',
      render: (_, item) => <Checkbox {...createCheckboxProps(item, PERMISSIONS.CATALOG_READ)} />,
    },
    {
      key: 'catalog_update',
      title: t.catalog.aclCatalogUpdate,
      align: 'center',
      render: (_, item) => <Checkbox {...createCheckboxProps(item, PERMISSIONS.CATALOG_UPDATE)} />,
    },
    {
      key: 'catalog_delete',
      title: t.catalog.aclCatalogDelete,
      align: 'center',
      render: (_, item) => <Checkbox {...createCheckboxProps(item, PERMISSIONS.CATALOG_DELETE)} />,
    },
    {
      key: 'item_update',
      title: t.catalog.aclItemUpdate,
      align: 'center',
      render: (_, item) => <Checkbox {...createCheckboxProps(item, PERMISSIONS.ITEM_UPDATE)} />,
    },
    {
      key: 'item_delete',
      title: t.catalog.aclItemDelete,
      align: 'center',
      render: (_, item) => <Checkbox {...createCheckboxProps(item, PERMISSIONS.ITEM_DELETE)} />,
    },
    {
      key: 'binding',
      title: t.catalog.aclBinding,
      align: 'center',
      render: (_, item) => <Checkbox {...createCheckboxProps(item, PERMISSIONS.BINDING)} />,
    },
    {
      key: 'actions',
      render: (_, item) => item.profileID !== ACL_OTHER && !store.profile.currentUserEmails.includes(item.profileID) && (
        <Button icon={<DeleteOutlined />} onClick={() => onDelete(item)} />
      ),
    },
  ]

  return (
    <div className={styles.root}>
      {!hideInput && (
        <InputAdd
          disabled={disabled}
          value={aclEmails}
          placeholder={'email'}
          validate={v => validateEmail(v)}
          onChange={onEmailsAdd}
        />
      )}
      {!hideTable && (
        <Table
          className={'mt2'}
          idField={'profileID'}
          items={value}
          columns={columns}
        />
      )}
    </div>
  )
}

const defaultPermissions = [
  'catalog_read',
  'catalog_update',
  'catalog_delete',
  'item_update',
  'item_delete',
  'binding',
]

/**
 * Immutable update
 * @param items
 * @param email
 * @param permission
 * @param checked
 */
function updateItem (items: IAcl[], email: string, permission: PERMISSIONS, checked: boolean): IAcl[] {
  return produce(items, items => {
    const item = items.find(i => i.profileID === email)
    if (item) {
      item.permissions = checked
        ? [...new Set([...item.permissions, permission])]
        : item.permissions.filter(i => i !== permission)
    }
  })
}
